import { forwardRef, useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, XCircle, AlertCircle, Info, X } from 'lucide-react';
import { cn } from '@/lib/utils';

export type FeedbackType = 'success' | 'error' | 'warning' | 'info';

interface InlineFeedbackProps {
  type: FeedbackType;
  message: string;
  show: boolean;
  onClose?: () => void;
  className?: string;
}

const icons = {
  success: CheckCircle2,
  error: XCircle,
  warning: AlertCircle,
  info: Info,
};

const styles = {
  success: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-600 dark:text-emerald-400',
  error: 'bg-red-500/10 border-red-500/30 text-red-600 dark:text-red-400',
  warning: 'bg-amber-500/10 border-amber-500/30 text-amber-600 dark:text-amber-400',
  info: 'bg-blue-500/10 border-blue-500/30 text-blue-600 dark:text-blue-400',
};

const iconStyles = {
  success: 'text-emerald-500',
  error: 'text-red-500',
  warning: 'text-amber-500',
  info: 'text-blue-500',
};

export const InlineFeedback = forwardRef<HTMLDivElement, InlineFeedbackProps>(
  ({ type, message, show, onClose, className }, ref) => {
    const Icon = icons[type];

    if (!show) return null;

    return (
      <motion.div
        ref={ref}
        initial={{ opacity: 0, y: -10, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -10, scale: 0.95 }}
        transition={{ 
          type: 'spring', 
          stiffness: 500, 
          damping: 30,
          mass: 0.8
        }}
        className={cn(
          'flex items-center gap-3 px-4 py-3 rounded-xl border backdrop-blur-sm',
          styles[type],
          className
        )}
      >
        <motion.div
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ type: 'spring', stiffness: 500, damping: 25, delay: 0.1 }}
        >
          <Icon className={cn('w-5 h-5 shrink-0', iconStyles[type])} />
        </motion.div>
        
        <motion.span 
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.15 }}
          className="text-sm font-medium flex-1"
        >
          {message}
        </motion.span>
        
        {onClose && (
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-black/10 dark:hover:bg-white/10 transition-colors"
          >
            <X className="w-4 h-4" />
          </motion.button>
        )}
      </motion.div>
    );
  }
);

InlineFeedback.displayName = 'InlineFeedback';

// Hook for managing feedback state
interface FeedbackState {
  show: boolean;
  type: FeedbackType;
  message: string;
}

export const useInlineFeedback = (autoHideMs = 5000) => {
  const [feedback, setFeedback] = useState<FeedbackState>({
    show: false,
    type: 'info',
    message: '',
  });

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const showFeedback = useCallback(
    (type: FeedbackType, message: string) => {
      setFeedback({ show: true, type, message });
      clearTimer();
      if (autoHideMs > 0) {
        timerRef.current = setTimeout(() => {
          setFeedback((prev) => ({ ...prev, show: false }));
        }, autoHideMs);
      }
    },
    [autoHideMs, clearTimer]
  );

  const hideFeedback = useCallback(() => {
    clearTimer();
    setFeedback((prev) => ({ ...prev, show: false }));
  }, [clearTimer]);

  useEffect(() => {
    return () => {
      clearTimer();
    };
  }, [clearTimer]);

  const success = useCallback((msg: string) => showFeedback('success', msg), [showFeedback]);
  const error = useCallback((msg: string) => showFeedback('error', msg), [showFeedback]);
  const warning = useCallback((msg: string) => showFeedback('warning', msg), [showFeedback]);
  const info = useCallback((msg: string) => showFeedback('info', msg), [showFeedback]);

  // Memoize to keep stable references (prevents useEffect loops in pages that depend on this object)
  return useMemo(
    () => ({
      feedback,
      showFeedback,
      hideFeedback,
      success,
      error,
      warning,
      info,
    }),
    [feedback, showFeedback, hideFeedback, success, error, warning, info]
  );
};

// --- PAGE-LEVEL INLINE FEEDBACK (Golden standard: Danh sách dự án)

export const PAGE_FEEDBACK_MESSAGES = {
  createSuccess: 'Tạo thành công',
  updateSuccess: 'Cập nhật thành công',
  deleteSuccess: 'Xoá thành công',
  errorDefault: 'Không thể thực hiện. Vui lòng thử lại.',
} as const;

export type PageSuccessType = 'create' | 'update' | 'delete';

/**
 * Hook for page-level inline feedback with STANDARD messages only.
 * Use on list pages (Danh sách dự án, Báo cáo công việc, Sự cố, ...).
 * Renders same banner as Project List (full-width, icon left, text-sm, close button).
 */
export const usePageInlineFeedback = (autoHideMs = 5000) => {
  const base = useInlineFeedback(autoHideMs);

  const showSuccess = useCallback(
    (type: PageSuccessType) => {
      const msg =
        type === 'create'
          ? PAGE_FEEDBACK_MESSAGES.createSuccess
          : type === 'update'
            ? PAGE_FEEDBACK_MESSAGES.updateSuccess
            : PAGE_FEEDBACK_MESSAGES.deleteSuccess;
      base.success(msg);
    },
    [base.success]
  );

  const showError = useCallback(
    (message?: string) => {
      base.error(message || PAGE_FEEDBACK_MESSAGES.errorDefault);
    },
    [base.error]
  );

  /** Custom success message (e.g. import: "Đã nhập N dự án thành công"). */
  const showSuccessMessage = useCallback((msg: string) => base.success(msg), [base.success]);

  return useMemo(
    () => ({
      feedback: base.feedback,
      hideFeedback: base.hideFeedback,
      showSuccess,
      showSuccessMessage,
      showError,
    }),
    [base.feedback, base.hideFeedback, showSuccess, showSuccessMessage, showError]
  );
};

/** Re-export for list pages: same UX as "Tạo dự án thành công" banner. Use with usePageInlineFeedback. */
export const PageInlineFeedback = InlineFeedback;